import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { apiClient } from "@/lib/apiClient";
import { getErrorMessage, isAuthError } from "@/lib/api";
import { TranslationMode } from "../../types";

interface EncryptionPanelProps {
  selectedCipherCode: string;
  mode: TranslationMode;
}

export default function EncryptionPanel({
  selectedCipherCode,
  mode,
}: EncryptionPanelProps) {
  const navigate = useNavigate();
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [hasCopied, setHasCopied] = useState(false);

  const { data: cipher } = useQuery({
    queryKey: ["cipher", selectedCipherCode],
    queryFn: () => apiClient.getCipher(selectedCipherCode),
    enabled: !!selectedCipherCode,
  });

  useEffect(() => {
    setInputText("");
    setOutputText("");
    setError(null);
  }, [mode]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (mode === "encrypt") {
        const result = await apiClient.encode({
          cipher_code: selectedCipherCode,
          plaintext: inputText,
        });
        return result.encoded;
      }
      const result = await apiClient.decode({
        cipher_code: selectedCipherCode,
        emojitext: inputText,
      });
      return result.decoded;
    },
    onSuccess: (result) => {
      setOutputText(result);
      toast.success(mode === "encrypt" ? "Text encrypted!" : "Text decrypted!");
    },
    onError: (err: unknown) => {
      const message = getErrorMessage(err);
      setError(message);
      setOutputText("");
      if (isAuthError(err)) {
        toast.error("Session expired. Please sign in again.");
        navigate("/login");
        return;
      }
      toast.error(message);
    },
  });

  const handleProcess = () => {
    if (!selectedCipherCode || !inputText) {
      setError("Please select a cipher and enter text to process");
      toast.error("Please select a cipher and enter text");
      return;
    }
    if (!cipher) {
      setError("Cipher not found. Please select a valid cipher.");
      toast.error("Invalid cipher selected");
      return;
    }
    setError(null);
    mutation.mutate();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (mode === "encrypt") {
      const cleanedText = newValue.replace(
        /[\uD800-\uDBFF][\uDC00-\uDFFF]|[☀-⟿]/g,
        "",
      );
      setInputText(cleanedText);
    } else {
      setInputText(newValue);
    }
    if (error) setError(null);
  };

  const handleCopy = async () => {
    if (outputText) {
      await navigator.clipboard.writeText(outputText);
      setHasCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setHasCopied(false), 2000);
    }
  };

  if (error) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          <p className="text-center text-red-500">{error}</p>
          <div className="flex justify-center">
            <Button onClick={() => setError(null)} variant="outline">
              Try Again
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const isLoading = mutation.isPending;

  return (
    <Card className="p-6 space-y-6">
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          <p className="text-sm text-gray-500">
            {mode === "encrypt" ? "Encrypting..." : "Decrypting..."}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {mode === "encrypt" ? "Plain Text" : "Emoji Input"}
            </label>
            <Textarea
              value={inputText}
              onChange={handleInputChange}
              placeholder={
                mode === "encrypt"
                  ? "Type or paste your message here..."
                  : "Paste encrypted emoji message here..."
              }
              className="min-h-[200px] font-mono"
              disabled={!cipher}
            />
            <p className="text-xs text-gray-500">
              {mode === "encrypt"
                ? "Case and special characters will be preserved. Only emojis will be removed from input."
                : "Paste your encrypted emoji message here to decrypt it."}
            </p>
          </div>

          <div className="flex justify-center">
            <Button
              onClick={handleProcess}
              className="px-8 bg-purple-600 hover:bg-purple-700"
              disabled={!inputText || isLoading || !cipher}
              size="lg"
            >
              {mode === "encrypt" ? "🔒 Emcrypt" : "🔓 Decrypt"}
            </Button>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {mode === "encrypt" ? "Emoji Output" : "Text Output"}
              </label>
              {outputText && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 px-3 text-gray-500 hover:text-gray-700"
                  onClick={handleCopy}
                >
                  {hasCopied ? (
                    <>
                      <Check className="h-4 w-4 text-green-500 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy
                    </>
                  )}
                </Button>
              )}
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-md font-mono break-all min-h-[200px] border-2 border-dashed border-gray-300 dark:border-gray-600">
              {outputText || (
                <span className="text-gray-400">
                  {mode === "encrypt"
                    ? "Encrypted message will appear here..."
                    : "Decrypted message will appear here..."}
                </span>
              )}
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
